import Image from 'next/image';
import { Fragment, type ReactNode } from 'react';
import { VerifiedBadge, type VerifiedBadgeSize } from '@/components/shared/VerifiedBadge';
import { cn } from '@/lib/utils';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import type { TweetAuthorAffiliation } from '@/types/tweet';

type VerifiedType = 'none' | 'blue' | 'business' | 'government';

interface ProfileIdentityBadgesProperties {
  affiliations?: TweetAuthorAffiliation[] | undefined;
  className?: string | undefined;
  renderAffiliation?:
    | ((details: {
        username?: string | undefined;
        content: ReactNode;
        affiliation: TweetAuthorAffiliation;
      }) => ReactNode)
    | undefined;
  size?: VerifiedBadgeSize | undefined;
  verifiedType: VerifiedType;
}

const BADGE_DIMENSIONS: Record<VerifiedBadgeSize, number> = {
  sm: 14,
  md: 18,
  lg: 22,
};

const getBadgeDimension = (size: VerifiedBadgeSize): number => BADGE_DIMENSIONS[size];

function normalizeAffiliations(affiliations?: TweetAuthorAffiliation[]): TweetAuthorAffiliation[] {
  if (!Array.isArray(affiliations)) return [];
  const seen = new Set<string>();
  const result: TweetAuthorAffiliation[] = [];
  for (const affiliation of affiliations) {
    if (!affiliation?.organizationProfileId) continue;
    if (seen.has(affiliation.organizationProfileId)) continue;
    seen.add(affiliation.organizationProfileId);
    result.push(affiliation);
  }
  return result;
}

export function ProfileIdentityBadges({
  verifiedType,
  affiliations,
  size = 'md',
  className,
  renderAffiliation,
}: ProfileIdentityBadgesProperties) {
  const normalizedAffiliations = normalizeAffiliations(affiliations);
  const showVerified = verifiedType !== 'none';
  const dimension = getBadgeDimension(size);

  if (!showVerified && normalizedAffiliations.length === 0) {
    return null;
  }

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {showVerified ? <VerifiedBadge size={size} type={verifiedType} /> : null}
      {normalizedAffiliations.map((affiliation, index) => {
        const badgeLabel = affiliation.organizationName
          ? `Affiliated with ${affiliation.organizationName}`
          : 'Affiliated organization';
        const badgeImage = affiliation.badgeUrl ? (
          <>
            {/* Affiliation badges live on Twitter CDN; avoid routing through Vercel optimizer to keep costs down */}
            <Image
              src={affiliation.badgeUrl}
              alt={badgeLabel}
              width={dimension}
              height={dimension}
              className="inline-block rounded"
              loading="lazy"
              unoptimized
            />
          </>
        ) : (
          <span className="text-xs text-muted-foreground" title={badgeLabel}>
            {affiliation.organizationName ?? 'Affiliated'}
          </span>
        );
        const content = (
          <span className="inline-flex items-center" role="img" aria-label={badgeLabel} title={badgeLabel}>
            {badgeImage}
          </span>
        );
        const username = affiliation.organizationUsername
          ? stripHandlePrefixes(affiliation.organizationUsername)
          : undefined;
        const key = `${affiliation.organizationProfileId}-${index}`;
        const rendered = renderAffiliation ? renderAffiliation({ username, content, affiliation }) : content;
        return <Fragment key={key}>{rendered}</Fragment>;
      })}
    </span>
  );
}
